/**
 * Room codes and guest display names.
 *
 * `docs/DESIGN.md`, "Netcode contract": "Room codes and guest display names;
 * accounts are optional and later." There is no database and no account
 * anywhere in this package — a code is a short string a person reads aloud,
 * and a name is a short string a person types.
 *
 * Both live here rather than in `apps/server` because the client validates
 * them too: a typo in a join code should be caught in the input box, using the
 * exact same alphabet the server generates from, not by a round trip.
 */

/**
 * The room-code alphabet: 26 letters and 10 digits, minus everything that gets
 * misread or mistyped when someone reads a code out over a voice call.
 *
 * Dropped: `I`, `1` and `L` (a sans-serif column of near-identical glyphs),
 * `O` and `0`, `S` and `5`, `Z` and `2`, `B` and `8`, `U` and `V`. What is left
 * is 23 symbols, every one of which survives being spoken, written on a sticky
 * note, or typed in the wrong case.
 */
export const ROOM_CODE_ALPHABET = 'ACDEFGHJKMNPQRTWXY34679';

/** Length of a room code. 23^4 ≈ 280k codes — far more than one process will ever hold at once. */
export const ROOM_CODE_LENGTH = 4;

const ROOM_CODE_PATTERN = new RegExp(`^[${ROOM_CODE_ALPHABET}]{${String(ROOM_CODE_LENGTH)}}$`);

/**
 * The excluded characters that are a kept character with a stroke missing: a
 * `J` read without its hook is an `I`, a `1` or a lower-case `l`; a `Q`
 * without its tail is an `O` or a `0`; half of a `W` is a `V` or a `U`.
 *
 * The other excluded characters — `S` and `5`, `Z` and `2`, `B` and `8` — look
 * like each other and like nothing the alphabet kept, so they are not here:
 * there is no code they could be a misreading of, and guessing one would send
 * a mistyped code to somebody else's room.
 */
const ROOM_CODE_FOLDS: Readonly<Record<string, string>> = {
  I: 'J',
  '1': 'J',
  L: 'J',
  O: 'Q',
  '0': 'Q',
  U: 'W',
  V: 'W',
};

/**
 * Fold a typed code into canonical form: trim, uppercase, drop the spaces and
 * hyphens someone typed for readability, and map a character that is a
 * misreading of one the alphabet kept (`ROOM_CODE_FOLDS`) back onto it.
 *
 * Someone who reads `PQ47` back as `pq47`, or types an `O` for the `Q`, should
 * still get into the room. This is the one place that leniency lives. A
 * character that folds onto nothing is left as it was typed, which
 * `isRoomCode` then refuses; everything downstream deals only in canonical
 * codes.
 */
export function normaliseRoomCode(input: string): string {
  return [...input.trim().toUpperCase().replace(/[\s-]/g, '')]
    .map((character) => ROOM_CODE_FOLDS[character] ?? character)
    .join('');
}

export function isRoomCode(value: string): boolean {
  return ROOM_CODE_PATTERN.test(value);
}

/**
 * Generate a room code from a supplied source of randomness.
 *
 * `random` is a parameter rather than a call to `Math.random` so this stays a
 * pure function: the server passes `Math.random`, and a test passes a
 * sequence, which is how `apps/server/test/lobby.test.ts` can assert collision
 * handling without hoping for a collision.
 */
export function generateRoomCode(random: () => number): string {
  let code = '';
  for (let i = 0; i < ROOM_CODE_LENGTH; i++) {
    const index = Math.min(
      ROOM_CODE_ALPHABET.length - 1,
      Math.floor(random() * ROOM_CODE_ALPHABET.length),
    );
    code += ROOM_CODE_ALPHABET[index] ?? ROOM_CODE_ALPHABET[0];
  }
  return code;
}

export const DISPLAY_NAME_MAX_LENGTH = 16;

/**
 * Control characters and invisible formatting marks: C0, DEL and C1, the
 * zero-width and bidi-mark block, the bidi embeddings and overrides, the
 * directional isolates, the line and paragraph separators, and the
 * byte-order mark. Every one of them renders as nothing, and several of
 * them reorder the text around them — a way to break someone else's lobby
 * list rather than a way to spell a name.
 *
 * `no-control-regex` is disabled because naming those characters is the
 * entire point of the expression.
 */
/* eslint-disable no-control-regex -- naming the characters is the entire point. */
const INVISIBLE =
  /[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u202a-\u202e\u2066-\u2069\u2028\u2029\ufeff]/gu;
/* eslint-enable no-control-regex */

/**
 * Clean a guest display name.
 *
 * Guests are anonymous, so there is nothing to authenticate — but the name is
 * rendered into another player's UI, so it is normalised to something short
 * and printable here, on the server's side of the wire, and never trusted as
 * typed. Invisible characters are dropped; runs of whitespace collapse to one
 * space; the result is capped.
 *
 * Returns `null` when nothing usable is left, which the caller turns into a
 * refusal rather than silently inventing a name.
 */
export function sanitiseDisplayName(input: unknown): string | null {
  if (typeof input !== 'string') return null;
  const cleaned = input
    .replace(INVISIBLE, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, DISPLAY_NAME_MAX_LENGTH);
  return cleaned.length > 0 ? cleaned : null;
}

/** A player seat, or a spectator. `docs/DESIGN.md`: "spectator slots that receive state and send nothing". */
export type ClientRole = 'player' | 'spectator';

/** Which side of the well a player is on. Two seats per duel. */
export type Seat = 0 | 1;

export const SEATS: readonly Seat[] = [0, 1];

export function otherSeat(seat: Seat): Seat {
  return seat === 0 ? 1 : 0;
}

/**
 * The seat a co-op room's one shared board is relayed under.
 *
 * A duel relays two boards, one per seat. Co-op has one, and it needs a seat
 * number on the wire because `SyncMessage` is shaped around seats — so it takes
 * the lower one, always, and the other seat never appears in a `sync`. The
 * events inside carry the slot they belong to, which is the part that actually
 * distinguishes the two players.
 */
export const COOP_RELAY_SEAT: Seat = 0;

/**
 * What kind of match a room is running.
 *
 * The only thing it decides today is the reconnect grace window —
 * `docs/DESIGN.md`: "the seat is held for 30 seconds in a friendly match, 15 in
 * ranked" — and what happens when that window runs out: a friendly match
 * freezes and stays resumable, a ranked one forfeits.
 *
 * No ranked match can be created yet; there is no rating, no queue and no
 * account to attach one to. The field exists and defaults to `friendly` so that
 * the *behaviour difference* is built, tested and settled now, rather than
 * being retrofitted into the reconnection path by a later phase that would have
 * to touch every branch of it.
 */
export type MatchType = 'friendly' | 'ranked';

export function isMatchType(value: unknown): value is MatchType {
  return value === 'friendly' || value === 'ranked';
}

/** A match type off the wire or out of a request, defaulted rather than refused. */
export function parseMatchType(value: unknown): MatchType {
  return isMatchType(value) ? value : 'friendly';
}

/**
 * Where a seat's occupant is, as far as the server can tell.
 *
 * - `online` — connected and playing.
 * - `paused` — connected, but the player told us their page went to the
 *   background before it froze. `docs/DESIGN.md`, PWA requirements: "Pause on
 *   `visibilitychange`, and tell the server before the socket dies."
 * - `reconnecting` — the socket is gone and the grace window is counting down.
 * - `gone` — the seat's occupant left for good: a consented quit, or a ranked
 *   grace window that ran out.
 *
 * `paused` and `reconnecting` are both "not receiving", and since owner
 * overhaul part 9 they have the same consequence: **the whole match stops.** A
 * player whose page is frozen can no more cancel a batch than one whose socket
 * is dead, and the amendment's answer to both is the same — nothing lands,
 * because nothing runs. Phase 05 owed the garbage instead and let the other
 * player play on; `docs/decisions/0041` retires that.
 */
export type SeatPresence = 'online' | 'paused' | 'reconnecting' | 'gone';
